/**
 * EmailTagInput — shared tag-based email input with contact autocomplete.
 * Used in Composer (reply/forward) and ComposeNew (new email).
 *
 * Behaviour:
 *  - Any single character triggers a contact search (debounced 220 ms).
 *  - Dropdown shows name + email + company; navigate with ↑↓, confirm with
 *    Enter / Tab, dismiss with Escape.
 *  - Works for any script (Latin, CJK, Thai, Japanese, etc.) because SQLite
 *    LIKE '%q%' matches any Unicode substring.
 *  - Enter / comma commits the raw value when no dropdown item is highlighted.
 *  - Backspace with empty input removes the last tag.
 */
import React, { useState, useEffect, useRef } from 'react';
import { displayCompanyName } from '../utils/displayName';
import { apiFetch } from '../utils/api';

interface ContactSuggestion {
  id: string;
  name: string;
  email: string;
  company: string;
  visibility?: string;
}

interface Props {
  label: string;
  values: string[];
  onAdd: (v: string) => void;
  onRemove: (v: string) => void;
  placeholder?: string;
  /** No side label and no underline — for a form that draws its own label and box (Calendar's Guests). */
  bare?: boolean;
}

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
function getToken() { return localStorage.getItem('mf_token') || ''; }

export default function EmailTagInput({ label, values, onAdd, onRemove, placeholder, bare }: Props) {
  const [val, setVal] = useState('');
  const [suggestions, setSuggestions] = useState<ContactSuggestion[]>([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  /* ── Fetch suggestions on every keystroke (debounced) ── */
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const q = val.trim();
    if (!q) { setSuggestions([]); setActiveIdx(-1); return; }
    timerRef.current = setTimeout(async () => {
      try {
        const res = await apiFetch(
          `${BASE}/contacts/search?q=${encodeURIComponent(q)}`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        if (!res.ok) return;
        const data: ContactSuggestion[] = await res.json();
        setSuggestions(data.filter(s => s.email && !values.includes(s.email)));
        setActiveIdx(-1);
      } catch { /* network error — silently ignore */ }
    }, 220);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [val]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Close dropdown on outside click ── */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setSuggestions([]); setActiveIdx(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const commitSuggestion = (s: ContactSuggestion) => {
    if (s.email && !values.includes(s.email)) onAdd(s.email);
    setVal(''); setSuggestions([]); setActiveIdx(-1);
  };

  const commitRaw = (raw: string) => {
    const v = raw.trim().replace(/,$/, '');
    if (v && !values.includes(v)) onAdd(v);
    setVal(''); setSuggestions([]); setActiveIdx(-1);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, -1)); return; }
      if (e.key === 'Escape')    { setSuggestions([]); setActiveIdx(-1); return; }
      if ((e.key === 'Enter' || e.key === 'Tab') && activeIdx >= 0) {
        e.preventDefault(); commitSuggestion(suggestions[activeIdx]); return;
      }
    }
    if ((e.key === 'Enter' || e.key === ',') && val.trim()) { e.preventDefault(); commitRaw(val); return; }
    if (e.key === 'Backspace' && !val && values.length) onRemove(values[values.length - 1]);
  };

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', borderBottom: bare ? 'none' : '1px solid var(--border)', minHeight: 34, padding: '2px 0' }}
    >
      {/* Field label */}
      {!bare && (
        <span style={{
          fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)',
          width: 36, flexShrink: 0, paddingTop: 7, textAlign: 'right', marginRight: 10,
          textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          {label}
        </span>
      )}

      {/* Tags + input */}
      <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, paddingTop: 4 }}>
        {values.map(v => (
          <span key={v} style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            background: 'var(--c-gray-100)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', padding: '2px 7px', fontSize: 11,
          }}>
            {v}
            <button
              onClick={() => onRemove(v)}
              style={{ border: 'none', background: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 0, display: 'flex' }}
            >
              <i className="ti ti-x" style={{ fontSize: 10 }} aria-hidden />
            </button>
          </span>
        ))}
        <input
          value={val}
          onChange={e => setVal(e.target.value)}
          /* Commit whatever is still typed when the field loses focus. Without this, typing an
             address and clicking Send straight away silently DISCARDED it: the text never became a
             chip, `to` went out empty, and the send died on nodemailer's "No recipients defined"
             with the message lost (owner hit this 2026-08-22). Blur fires before the Send click. */
          onBlur={() => { if (val.trim()) commitRaw(val); }}
          onKeyDown={handleKey}
          placeholder={values.length === 0 ? (placeholder || '') : ''}
          style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: 'var(--text-primary)', minWidth: 120, padding: '3px 0' }}
        />
      </div>

      {/* Autocomplete dropdown */}
      {suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: bare ? 0 : 36, right: 0, zIndex: 1200,
          background: 'var(--bg-surface)', border: '1px solid var(--border-strong)',
          borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)',
          overflow: 'hidden', marginTop: 3,
        }}>
          {suggestions.map((s, i) => (
            <div
              key={s.id}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseDown={e => { e.preventDefault(); commitSuggestion(s); }}
              style={{
                padding: '7px 12px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 10,
                background: i === activeIdx ? 'var(--accent-bg)' : 'transparent',
                borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              {/* Avatar — colour by visibility; the letter trimmed to its cap height so the flex box centres the capital
                  itself (Bugify #836d4443, 2nd opening) */}
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0, transition: 'all 0.1s',
                background: i === activeIdx
                  ? (s.visibility === 'office' ? '#6D28D9' : 'var(--accent)')
                  : (s.visibility === 'office' ? '#EDE9FE' : 'var(--c-gray-100)'),
                color: i === activeIdx ? '#fff'
                  : (s.visibility === 'office' ? '#6D28D9' : 'var(--text-secondary)'),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
              }}>
                {s.name ? <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{displayCompanyName(s.name).charAt(0).toUpperCase()}</span> : <i className="ti ti-at" style={{ fontSize: 12 }} />}
              </div>
              {/* Info */}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {/* A company shows without its legal form (Calem, report fcc6685f); only the
                      address is ever added to the email. */}
                  {s.name && (
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {displayCompanyName(s.name)}
                    </span>
                  )}
                  {s.visibility === 'office' && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8, background: '#EDE9FE', color: '#6D28D9', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      <i className="ti ti-building" style={{ fontSize: 9, marginRight: 2 }} />Office
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.email}{s.company ? <span style={{ color: 'var(--text-tertiary)' }}> · {displayCompanyName(s.company)}</span> : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
